// File: src/components/onboarding/WelcomeHub.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './WelcomeHub.css';

const ICON_PROPS = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 };

const CheckIcon = () => (
  <svg {...ICON_PROPS} strokeWidth={2.5}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ClockIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const PreviewIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const XIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

/**
 * WelcomeHub
 *
 * Replaces the empty dashboard for first-time users until tracker data flows.
 * Designed to address the four reviewer concerns at once:
 *   1. Confidence that setup worked (live checklist with checkmarks)
 *   2. What to expect next (waiting timeline + preview cards)
 *   3. Real-time confirmation (visitor counter — fed by parent's data)
 *   4. Quick win action (test-visit link to their own store)
 *
 * Props:
 *   storeName     — display name for greeting ("Pizza Website" or "your store")
 *   storeUrl      — the merchant's storefront URL (optional, used for test-visit link)
 *   firstName     — user's first name if available
 *   visitorCount  — number from tracker (0 by default; rises live as visits arrive)
 *   onDismiss     — fired when user clicks "Got it, take me to the dashboard"
 *   onExploreNav  — fired when user clicks "Show me around" — triggers nav coachmark
 */
export default function WelcomeHub({
  storeName = 'your store',
  storeUrl = null,
  firstName = null,
  visitorCount = 0,
  onDismiss,
  onExploreNav,
}) {
  const navigate = useNavigate();

  // Animate the visitor counter when it changes
  const [displayCount, setDisplayCount] = useState(visitorCount);
  useEffect(() => {
    setDisplayCount(visitorCount);
  }, [visitorCount]);

  const greeting = firstName ? `Welcome, ${firstName}!` : 'Welcome to BaoBrain!';
  const VISITOR_TARGET = 100;
  const progressPct = Math.min(100, (visitorCount / VISITOR_TARGET) * 100);

  // Setup checklist — all green except the live "waiting" step
  const checklist = [
    { label: 'Installed BaoBrain', done: true },
    { label: 'Connected your store', done: true },
    { label: 'Tracking is live', done: true },
    {
      label: visitorCount === 0
        ? `Waiting for your first visitor`
        : `Collecting visitor data (${displayCount} of ${VISITOR_TARGET})`,
      done: visitorCount >= VISITOR_TARGET,
      inProgress: visitorCount < VISITOR_TARGET,
    },
    { label: 'See your first insights', done: false },
  ];

  const previewCards = [
    {
      title: 'Customer Intelligence',
      description: 'Who buys, why they buy, what makes them stick',
      route: '/business',
      eta: 'After ~50 sessions',
    },
    {
      title: 'Experience & Conversion',
      description: 'Where friction happens, how to fix it',
      route: '/performance',
      eta: 'After ~100 sessions',
    },
    {
      title: 'Growth & Strategy',
      description: "What's working, what to do next",
      route: '/facts',
      eta: 'After your first orders',
    },
  ];

  const handleTestVisit = () => {
    if (storeUrl) {
      window.open(storeUrl.startsWith('http') ? storeUrl : `https://${storeUrl}`, '_blank');
    }
  };

  const handleExploreNav = () => {
    if (onExploreNav) onExploreNav();
    if (onDismiss) onDismiss();
  };

  return (
    <div className="bao-welcome-hub">
      {/* Close button */}
      {onDismiss && (
        <button
          className="bao-welcome-close"
          onClick={onDismiss}
          aria-label="Dismiss welcome screen"
          type="button"
        >
          <XIcon />
        </button>
      )}

      {/* Header */}
      <div className="bao-welcome-header">
        <h1 className="bao-welcome-title">{greeting}</h1>
        <p className="bao-welcome-subtitle">
          {visitorCount === 0
            ? <>You're all set up on <strong>{storeName}</strong>. Here's what happens next.</>
            : <>You're collecting data from <strong>{storeName}</strong> — first insights are on the way.</>}
        </p>
      </div>

      {/* Setup Checklist */}
      <section className="bao-welcome-section">
        <h2 className="bao-welcome-section-title">Your setup progress</h2>
        <ul className="bao-welcome-checklist">
          {checklist.map((item, i) => (
            <li
              key={i}
              className={`bao-welcome-checklist-item ${item.done ? 'is-done' : ''} ${item.inProgress ? 'is-in-progress' : ''}`}
            >
              <span className="bao-welcome-checklist-icon" aria-hidden="true">
                {item.done ? (
                  <CheckIcon />
                ) : item.inProgress ? (
                  <ClockIcon />
                ) : (
                  <span className="bao-welcome-checklist-dot" />
                )}
              </span>
              <span className="bao-welcome-checklist-label">{item.label}</span>
              {item.inProgress && (
                <div className="bao-welcome-progress-bar">
                  <div
                    className="bao-welcome-progress-fill"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* Quick win — test visit */}
      {storeUrl && visitorCount === 0 && (
        <section className="bao-welcome-quickwin">
          <div className="bao-welcome-quickwin-icon">
            <PreviewIcon />
          </div>
          <div className="bao-welcome-quickwin-content">
            <p className="bao-welcome-quickwin-title">Want to see it work right now?</p>
            <p className="bao-welcome-quickwin-text">
              Visit your own store — you'll show up here as the first session within seconds.
            </p>
          </div>
          <button
            className="bao-welcome-quickwin-btn"
            onClick={handleTestVisit}
            type="button"
          >
            Visit my store <ExternalLinkIcon />
          </button>
        </section>
      )}

      {/* Preview cards — what they'll see */}
      <section className="bao-welcome-section">
        <h2 className="bao-welcome-section-title">What you'll see here</h2>
        <p className="bao-welcome-section-sub">
          BaoBrain is organized into three areas. Each fills in as we collect data.
        </p>
        <div className="bao-welcome-previews">
          {previewCards.map((card) => (
            <button
              key={card.route}
              className="bao-welcome-preview-card"
              onClick={() => navigate(card.route)}
              type="button"
            >
              <span className="bao-welcome-preview-eta">{card.eta}</span>
              <h3 className="bao-welcome-preview-title">{card.title}</h3>
              <p className="bao-welcome-preview-desc">{card.description}</p>
              <span className="bao-welcome-preview-link">
                Take a look <ArrowRightIcon />
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Footer actions */}
      <div className="bao-welcome-footer">
        <p className="bao-welcome-footer-text">
          Don't worry — you can come back to this dashboard any time. We'll let you know the moment your first insights are ready.
        </p>
        <div className="bao-welcome-footer-actions">
          <button
            className="bao-welcome-btn-secondary"
            onClick={handleExploreNav}
            type="button"
          >
            Show me around
          </button>
          <button
            className="bao-welcome-btn-primary"
            onClick={onDismiss}
            type="button"
          >
            Got it, take me to the dashboard
          </button>
        </div>
      </div>
    </div>
  );
}